import { useEffect, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Plus, Trash, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const emptyTx = { type: "income", category: "", description: "", amount: "", transaction_date: new Date().toISOString().slice(0, 10) };

function formatRupiah(value) {
    return `Rp ${Number(value || 0).toLocaleString("id-ID")}`;
}

export default function FinanceReportTab() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [report, setReport] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [form, setForm] = useState(emptyTx);
    const [saving, setSaving] = useState(false);

    const load = async (selectedMonth = month) => {
        if (!isAdmin) return;
        try {
            const query = selectedMonth ? `?month=${selectedMonth}` : "";
            const [reportRes, txRes] = await Promise.all([
                api.get(`/finance/report${query}`),
                api.get(`/finance/transactions${query}`),
            ]);
            setReport(reportRes.data);
            setTransactions(txRes.data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    useEffect(() => {
        load(new Date().toISOString().slice(0, 7));
    }, []);

    const changeMonth = (value) => {
        setMonth(value);
        load(value);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/finance/transactions", { ...form, amount: Number(form.amount), period_month: month });
            toast.success("Transaksi tersimpan");
            setForm(emptyTx);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (tx) => {
        if (!window.confirm(`Hapus transaksi "${tx.description}"?`)) return;
        try {
            await api.delete(`/finance/transactions/${tx.id}`);
            toast.success("Transaksi dihapus");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    if (!isAdmin) {
        return (
            <div className="space-y-8" data-testid="finance-report-tab">
                <div>
                    <h2 className="font-display uppercase text-2xl">Input Pemasukan / Pengeluaran</h2>
                    <p className="text-sm text-racing-muted mt-2">Admin Keuangan dapat menginput pemasukan dan pengeluaran lain. Laporan keuangan keseluruhan hanya dapat diakses oleh Admin.</p>
                </div>

                <section className="bg-white border border-racing-line p-6">
                    <h3 className="font-display uppercase text-xl mb-5">Tambah Pemasukan / Pengeluaran Lain</h3>
                    <form onSubmit={submit} className="grid md:grid-cols-6 gap-4" data-testid="finance-transaction-form">
                        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="finance-type-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm">
                            <option value="income">Pemasukan</option><option value="expense">Pengeluaran</option>
                        </select>
                        <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="finance-category-input" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Kategori" />
                        <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="finance-description-input" className="md:col-span-2 bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Keterangan" />
                        <input required type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="finance-amount-input" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Nominal" />
                        <button disabled={saving} data-testid="finance-add-transaction" className="flex items-center justify-center gap-2 bg-racing-red text-white px-4 py-3 text-xs uppercase tracking-[0.2em]"><Plus size={16} /> Simpan</button>
                    </form>
                </section>
                <p className="flex items-center gap-2 text-xs text-racing-muted" data-testid="finance-restricted-note"><Wallet size={16} /> Transaksi yang Anda input akan masuk ke laporan keuangan yang dikelola Admin.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="finance-report-tab">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h2 className="font-display uppercase text-2xl">Laporan Keuangan</h2>
                    <p className="text-sm text-racing-muted mt-2">Iuran otomatis masuk sebagai pemasukan. Admin/Admin Keuangan dapat menambah pemasukan dan pengeluaran lain.</p>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Periode</label>
                    <div className="flex gap-2">
                        <input type="month" value={month} onChange={(e) => changeMonth(e.target.value)} data-testid="finance-month-input" className="bg-white border border-racing-line px-4 py-3 text-sm" />
                        <a href={`${api.defaults.baseURL}/finance/export?month=${month}&format=xlsx`} data-testid="finance-export-xlsx" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">Excel</a>
                        <a href={`${api.defaults.baseURL}/finance/export?month=${month}&format=pdf`} data-testid="finance-export-pdf" className="border border-racing-line px-4 py-3 text-xs uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red">PDF</a>
                    </div>
                </div>
            </div>

            {report && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-racing-line border border-racing-line" data-testid="finance-summary">
                    <div className="bg-white p-5"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Iuran</p><p className="font-display text-3xl mt-2">{formatRupiah(report.income_fee)}</p></div>
                    <div className="bg-white p-5"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Pemasukan Lain</p><p className="font-display text-3xl mt-2">{formatRupiah(report.income_other)}</p></div>
                    <div className="bg-white p-5"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Total Masuk</p><p className="font-display text-3xl mt-2 text-green-700">{formatRupiah(report.income_total)}</p></div>
                    <div className="bg-white p-5"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Pengeluaran</p><p className="font-display text-3xl mt-2 text-racing-red">{formatRupiah(report.expense_total)}</p></div>
                    <div className="bg-white p-5"><p className="text-[10px] uppercase tracking-[0.2em] text-racing-muted">Saldo</p><p className="font-display text-3xl mt-2 text-racing-golddeep">{formatRupiah(report.balance)}</p></div>
                </div>
            )}

            <section className="bg-white border border-racing-line p-6">
                <h3 className="font-display uppercase text-xl mb-5">Tambah Pemasukan / Pengeluaran Lain</h3>
                <form onSubmit={submit} className="grid md:grid-cols-6 gap-4" data-testid="finance-transaction-form">
                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="finance-type-select" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm">
                        <option value="income">Pemasukan</option><option value="expense">Pengeluaran</option>
                    </select>
                    <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="finance-category-input" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Kategori" />
                    <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="finance-description-input" className="md:col-span-2 bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Keterangan" />
                    <input required type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="finance-amount-input" className="bg-racing-surface border border-racing-line px-3 py-3 text-sm" placeholder="Nominal" />
                    <button disabled={saving} data-testid="finance-add-transaction" className="flex items-center justify-center gap-2 bg-racing-red text-white px-4 py-3 text-xs uppercase tracking-[0.2em]"><Plus size={16} /> Simpan</button>
                </form>
            </section>

            <section className="border border-racing-line overflow-x-auto bg-white">
                <table className="w-full text-sm min-w-[850px]">
                    <thead><tr className="border-b border-racing-line bg-racing-surface">{["Tanggal", "Tipe", "Kategori", "Keterangan", "Nominal", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted">{h}</th>)}</tr></thead>
                    <tbody>
                        {transactions.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center text-racing-muted" data-testid="finance-empty">Belum ada transaksi pada periode ini.</td></tr>}
                        {transactions.map((tx, index) => (
                            <tr key={tx.id} className="border-b border-racing-line last:border-0" data-testid={`finance-transaction-${index}`}>
                                <td className="px-4 py-3 text-xs text-racing-muted">{tx.transaction_date}</td>
                                <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 text-xs ${tx.type === "income" ? "text-green-700" : "text-racing-red"}`}>{tx.type === "income" ? <ArrowUpRight size={15} /> : <ArrowDownLeft size={15} />}{tx.type === "income" ? "Masuk" : "Keluar"}</span></td>
                                <td className="px-4 py-3">{tx.category}{tx.source === "iuran" ? " (otomatis)" : ""}</td>
                                <td className="px-4 py-3 max-w-[320px] truncate">{tx.description}</td>
                                <td className="px-4 py-3 font-medium">{formatRupiah(tx.amount)}</td>
                                <td className="px-4 py-3 text-right">{tx.source === "manual" && <button onClick={() => remove(tx)} data-testid={`finance-delete-${index}`} className="p-2 border border-racing-line text-racing-red"><Trash size={15} /></button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
            <p className="flex items-center gap-2 text-xs text-racing-muted"><Wallet size={16} /> Laporan menggabungkan iuran, pemasukan lain, dan pengeluaran.</p>
        </div>
    );
}
